import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../context/AuthContext';

export const PoReceiveDialog = ({ api, po, open, onOpenChange, onDone }) => {
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState('');
  const { user } = useAuth();
  const [by, setBy] = useState('');
  const [rdate, setRdate] = useState('');
  const [people, setPeople] = useState([]);
  useEffect(() => { if (open) api().get('/purchase-orders/receivers').then(r => setPeople(r.data)).catch(() => {}); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const remaining = (l) => (l.item_id ? Math.max(0, Number(l.quantity) - Number(l.received_qty || 0)) : 0);
  useEffect(() => { if (open) { setQty(Object.fromEntries(po.lines.map(l => [l.id, remaining(l)]))); setNotes(''); setBy(user?.full_name || user?.email || ''); setRdate(new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10)); } }, [open, po]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!by.trim() || !rdate) { toast.error('Enter who received it and the date'); return; }
    const lines = Object.entries(qty).map(([line_id, q]) => ({ line_id, quantity: Number(q) })).filter(x => x.quantity > 0);
    if (!lines.length) { toast.error('Enter a quantity to receive'); return; }
    setBusy(true);
    try {
      const r = await api().post(`/purchase-orders/${po.id}/receive`, { lines, notes: notes || null, received_by: by || null, received_date: rdate || null });
      toast.success(`Received. ${po.po_number} is now ${r.data.status}`); onDone(r.data); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Receive failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="po-receive-dialog">
        <DialogHeader><DialogTitle>Receive {po.po_number}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Stock lines are added to inventory with a restock transaction at the PO unit cost.</p>
        <div className="space-y-2">
          {po.lines.map((l, i) => (
            <div key={l.id} className="grid grid-cols-12 gap-2 items-center text-sm" data-testid={`po-receive-line-${i}`}>
              <span className="col-span-7 truncate">{l.item_sku ? <span className="mono mr-1">{l.item_sku}</span> : <span className="text-[10px] uppercase text-red-600 mr-1" data-testid={`po-receive-unlinked-${i}`}>not linked, link item first</span>}{l.description}</span>
              <span className="col-span-3 text-xs text-slate-500 text-right">{l.received_qty || 0} of {l.quantity} received</span>
              <Input type="number" min="0" max={remaining(l)} step="any" value={qty[l.id] ?? 0} disabled={!remaining(l)} onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })} className="col-span-2 h-8 rounded-sm mono text-right" data-testid={`po-receive-qty-${i}`} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Received by *</Label>
            <Input list="po-receivers" value={by} onChange={(e) => setBy(e.target.value)} className="rounded-sm" data-testid="po-receive-by" />
            <datalist id="po-receivers">{people.map(n => <option key={n} value={n} />)}</datalist>
          </div>
          <div><Label className="text-xs">Received date *</Label><Input type="date" value={rdate} onChange={(e) => setRdate(e.target.value)} className="rounded-sm" data-testid="po-receive-date" /></div>
        </div>
        <p className="text-[11px] text-slate-500">Received stock items are added to inventory (stocked) right away, at this PO's unit cost.</p>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Receiving notes (packing slip #, condition…)" className="rounded-sm" data-testid="po-receive-notes" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 rounded-sm" data-testid="po-receive-submit">{busy ? 'Receiving…' : 'Receive into stock'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PoReceiveDialog;
